import { useIntl } from "react-intl"
import { ColumnsHeader } from "components/Shared/InformationTable/interfaces"
import { concatClassNames } from "helpers/jsxHelpers"
import { getLevel, TabOptions } from "../utils"

export interface VSMResponse {
	breakdown: {
		metricName: string
		data: VSMData[]
	}
}
export interface VSMData {
	id: number
	name: string
	barcode: string
	subcategoryName: string
	currentSales: number
	previousSales: number
	penetrationRate: number
	spendingRate: number
	closingRate: number
}

const title =
	"phronesis.transactional.application.commercial.benchmark.title.column"

const levelsToShowColumns = ["stores", "products"]

const columnToSort = (levelName: string) =>
	levelName === "stores" ? "id" : "barcode"

export const mustShowMoreColumns = (currentTab: TabOptions) => {
	const levelName = getLevel(currentTab)
	return levelsToShowColumns.includes(levelName)
}

export const columnsHeader = (currentTab: TabOptions): Array<ColumnsHeader> => {
	const intl = useIntl()
	const levelName = getLevel(currentTab)
	const mustShowMoreColumns = levelsToShowColumns.includes(levelName)

	const levelColumn = {
		name: intl.formatMessage({ id: title }, { metric: levelName }),
		alignment: "left",
		nameColumnSort: "name",
		hasSort: true,
		className: "header",
	}

	const codeColumn = {
		name: intl.formatMessage({ id: title }, { metric: "subcategory" }),
		alignment: "left",
		nameColumnSort: "subcategoryName",
		hasSort: true,
		className: "header",
	}

	const idColumn = {
		name: intl.formatMessage({ id: title + ".id" }, { metric: levelName }),
		alignment: "left",
		nameColumnSort: columnToSort(levelName),
		hasSort: true,
		className: concatClassNames([
			"header",
			levelName === "products" ? "barcode-cell" : "code-cell",
		]),
	}

	const baseColumns = [
		{
			name: intl.formatMessage({
				id: title + ".sales",
			}),
			alignment: "left",
			nameColumnSort: "currentSales",
			hasSort: true,
			className: "header",
		},
		{
			name: intl.formatMessage({
				id: title + ".penetration",
			}),
			alignment: "right",
			nameColumnSort: "penetrationRate",
			hasSort: true,
			className: concatClassNames(["header", "penetration-cell"]),
		},
		{
			name: intl.formatMessage({
				id: title + ".closingRate",
			}),
			alignment: "right",
			nameColumnSort: "closingRate",
			hasSort: true,
			className: concatClassNames(["header", "rate-cell"]),
		},
		{
			name: intl.formatMessage({
				id: title + ".spendingRate",
			}),
			alignment: "right",
			nameColumnSort: "spendingRate",
			hasSort: true,
			className: concatClassNames(["header", "rate-cell"]),
		},
	]

	const columnsToShow: Record<string, Array<ColumnsHeader>> = {
		products: [levelColumn, codeColumn, idColumn, ...baseColumns],
		stores: [levelColumn, idColumn, ...baseColumns],
	}

	return mustShowMoreColumns
		? columnsToShow[levelName]
		: [levelColumn, ...baseColumns]
}
